<template>
    <div>
        <v-card class="pricelists" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('pricelists.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn color="white" class="minw100" outlined @click="openForm()">
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text class="card-body">
                <v-data-table
                    :headers="headers"
                    :items="pricelists"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    disable-sort
                    disable-pagination
                    hide-default-footer
                    class="operations striped"
                >
                    <template #item="{ item }">
                        <tr>
                            <td>{{ item.name }}</td>
                            <td>
                                <v-chip
                                    style="z-index: 0"
                                    small
                                    outlined
                                    :color="item.enabled ? 'success' : 'error'"
                                >
                                    {{ item.enabled ? $t('general.yes') : $t('general.no') }}
                                </v-chip>
                            </td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template #activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            :key="1"
                                            @click="openForm(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            :key="2"
                                            @click="deletePricelist(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <cod-dialog
            :show.sync="showForm"
            max-width="400"
        >
            <template slot="header">
                <span>{{ $tc('general.pricelist', 1) }}</span>
            </template>

            <div class="pa-5" slot="content">
                <v-form ref="form">
                    <v-text-field
                        v-model="form.name"
                        :label="$t('general.name')"
                        :error-messages="form.$errors.name"
                        @blur="form.validate('name')"
                        @input="delete form.$errors.name"
                    />

                    <v-switch
                        v-model="form.enabled"
                        class="mb-4"
                        hide-details
                        :label="$t('general.active')"
                        inset
                        right
                    />
                </v-form>
            </div>

            <template slot="footer">
                <v-btn
                    class="flex-1 mr-1"
                    outlined
                    color="primary"
                    :disabled="form.$busy"
                    @click="showForm = false"
                >
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn
                    color="primary"
                    depressed
                    :disabled="form.$busy"
                    :loading="form.$busy"
                    class="flex-1 ml-1"
                    @click="savePricelist()"
                >
                    {{ $t('general.save') }}
                </v-btn>
            </template>
        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';
import Pricelist from '@/models/Pricelist'
import { required } from 'vuelidate/lib/validators';

export default {
    components: {
        CodDialog
    },

    created() {
        loadData('/admin/load/pricelists').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            pricelists: [],
            loading: false,
            form: this.createForm(),
            showForm: false,
        };
    },

    validations: {
        form: {
            name: { required },
            enabled: { required },
        }
    },

    computed: {
        headers() {
            return [
                { text: this.$t('general.name'), value: 'name', sortable: false },
                { text: this.$t('general.active'), value: 'enabled', sortable: false },
                { text: '', sortable: false }
            ]
        }
    },

    methods: {
        onDataLoaded(data) {
            this.pricelists = data.pricelists.map(p => new Pricelist(p));
        },

        createForm(pricelist = null) {
            return new Form(this, 'form', {
                id: pricelist?.id || null,
                name: pricelist?.name || null,
                enabled: pricelist ? pricelist.enabled : true
            })
        },

        openForm(pricelist = null) {
            this.form = this.createForm(pricelist);
            this.showForm = true;
        },

        async savePricelist() {
            if (!this.form.validate()) return

            const method = this.form.id ? 'patch' : 'post';
            const url = '/admin/pricelists' + (this.form.id ? `/${this.form.id}` : '');

            const response = await ApiService[method](url, this.form);
            const pricelist = new Pricelist(response.data);
            const pricelistIndex = this.pricelists.findIndex(p => p.id === pricelist.id);

            if(pricelistIndex === -1) {
                this.pricelists.push(pricelist);
            } else {
                this.$set(this.pricelists, pricelistIndex, pricelist);
            }

            Pricelist.insert({ data: [response.data] });
            this.showForm = false;

            this.$snackbar(this.$t('pricelists.saved'), 'success');
        },

        deletePricelist(pricelist) {
            this.$confirm(
                this.$t('pricelists.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    this.showForm = false;

                    ApiService.delete(`/admin/pricelists/${pricelist.id}`, {}).then(response => {
                        this.pricelists = this.pricelists.filter(item => item.id !== pricelist.id);
                        this.$snackbar(this.$t('pricelists.deleted'), 'success');

                        pricelist.$delete()
                    })
                }
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.pricelists {
    max-width: 600px;

    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .card-body {
        margin-top: 16px;
    }
}
</style>
